import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchAdminAnnouncements,
  createAnnouncement,
  archiveAnnouncement,
} from "@/lib/api";
import UserBadge from "@/components/UserBadge";
import AdminToggle from "@/components/AdminToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, Megaphone, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminAnnouncements() {
  const [data, setData] = useState({ announcements: [], total_users: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "",
    body: "",
    cta_label: "",
    cta_url: "",
    ends_at: "",
    dismissible: true,
  });

  const load = async () => {
    try {
      setData(await fetchAdminAnnouncements());
    } catch (e) {
      toast.error("Load failed.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and body required.");
      return;
    }
    setSaving(true);
    try {
      await createAnnouncement({
        title: form.title,
        body: form.body,
        cta_label: form.cta_label || null,
        cta_url: form.cta_url || null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        dismissible: form.dismissible,
      });
      setForm({ title: "", body: "", cta_label: "", cta_url: "", ends_at: "", dismissible: true });
      await load();
      toast.success("Announcement published.");
    } catch (e) {
      toast.error("Publish failed.");
    } finally {
      setSaving(false);
    }
  };

  const archive = async (id) => {
    try {
      await archiveAnnouncement(id);
      await load();
      toast.success("Archived.");
    } catch (e) {
      toast.error("Archive failed.");
    }
  };

  return (
    <div className="min-h-screen">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-obsidian-600">
        <Link to="/admin" className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-100">
          <ChevronLeft className="h-3 w-3" /> Admin
        </Link>
        <div className="flex items-center gap-4"><AdminToggle /><UserBadge /></div>
      </header>

      <div className="max-w-4xl mx-auto px-6 md:px-12 pt-12 pb-24">
        <div className="flex items-center gap-3 mb-4">
          <Megaphone className="h-5 w-5 text-terracotta" />
          <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta">Announcements</p>
        </div>
        <h1 className="font-serif text-4xl text-bone-100 mb-10">Speak to everyone</h1>

        <div className="border border-obsidian-600 p-6 mb-12" data-testid="announcement-form">
          <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-4">New announcement</p>
          <Input
            data-testid="ann-title-input"
            placeholder="Title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="bg-obsidian-900 border-obsidian-600 text-bone-100 rounded-none mb-3"
          />
          <Textarea
            data-testid="ann-body-input"
            placeholder="Message body — Cormorant Garamond italic, whatever the users need to know."
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            rows={4}
            className="bg-obsidian-900 border-obsidian-600 text-bone-100 rounded-none mb-3"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <Input
              placeholder="CTA label (optional)"
              value={form.cta_label}
              onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
              className="bg-obsidian-900 border-obsidian-600 text-bone-100 rounded-none"
            />
            <Input
              placeholder="CTA URL (optional)"
              value={form.cta_url}
              onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
              className="bg-obsidian-900 border-obsidian-600 text-bone-100 rounded-none"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div>
              <p className="font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-500 mb-2">Ends (optional)</p>
              <Input
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                className="bg-obsidian-900 border-obsidian-600 text-bone-100 rounded-none"
              />
            </div>
            <label className="flex items-center gap-2 mt-6">
              <input
                type="checkbox"
                checked={form.dismissible}
                onChange={(e) => setForm({ ...form, dismissible: e.target.checked })}
              />
              <span className="font-sans text-xs uppercase tracking-[0.2em] text-bone-400">Users can dismiss</span>
            </label>
          </div>
          <Button
            data-testid="ann-publish-btn"
            onClick={submit}
            disabled={saving}
            className="rounded-none bg-terracotta hover:bg-terracotta-deep text-obsidian-900 font-sans uppercase tracking-wider text-xs px-6 py-5"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Megaphone className="h-4 w-4 mr-2" />}
            Publish
          </Button>
        </div>

        <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-4">Existing</p>
        {loading ? (
          <p className="font-sans text-bone-400 text-sm">Loading…</p>
        ) : (
          <div className="space-y-3" data-testid="announcement-list">
            {data.announcements.length === 0 && (
              <p className="font-sans text-bone-500 text-sm">No announcements yet.</p>
            )}
            {data.announcements.map((a) => (
              <div key={a.id} className={`border border-obsidian-600 p-5 ${a.archived_at ? "opacity-50" : ""}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="font-serif text-lg text-bone-100 mb-1">{a.title}</p>
                    <p className="font-sans text-sm text-bone-300 whitespace-pre-line mb-2">{a.body}</p>
                    <p className="font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-500">
                      Read by {a.dismissed_count} of {data.total_users}
                      {a.archived_at && " · archived"}
                    </p>
                  </div>
                  {!a.archived_at && (
                    <button
                      data-testid={`ann-archive-${a.id}`}
                      onClick={() => archive(a.id)}
                      className="text-bone-400 hover:text-terracotta p-2 transition-colors"
                      aria-label="Archive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
